import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Star, Loader2, CheckCircle2, Sparkles, ShieldCheck,
    Heart, XCircle, Home, User,
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

/**
 * Homly — Page publique de dépôt d'avis (magic-link)
 * ────────────────────────────────────────────────────
 * Le client (vendeur OU acquéreur) reçoit un lien magique par email
 * et débarque ici pour laisser son avis en 2 minutes.
 *
 * Pas d'auth : le token dans l'URL fait office d'authentification.
 */
export default function PublicReviewPage() {
    const { token } = useParams();
    const [invite, setInvite] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [dims, setDims] = useState({
        professionnalisme: 0, reactivite: 0, ecoute: 0, negociation: 0,
    });
    const [comment, setComment] = useState('');
    const [wouldRecommend, setWouldRecommend] = useState(null);
    const [consentPublic, setConsentPublic] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get(`/public/reviews/${token}`);
            setInvite(res.data);
        } catch {
            setNotFound(true);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const canSubmit =
        Object.values(dims).every(v => v > 0) &&
        comment.trim().length >= 10 &&
        wouldRecommend !== null &&
        consentPublic;

    // Barre de progression : combien de champs remplis (sur 4 dims + comment + rec + consent = 7)
    const filledCount =
        Object.values(dims).filter(v => v > 0).length +
        (comment.trim().length >= 10 ? 1 : 0) +
        (wouldRecommend !== null ? 1 : 0) +
        (consentPublic ? 1 : 0);
    const progress = Math.round((filledCount / 7) * 100);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            await api.post(`/public/reviews/${token}/submit`, {
                dimensions: dims,
                comment: comment.trim(),
                would_recommend: wouldRecommend,
                consent_public: consentPublic,
            });
            setSubmitted(true);
            toast.success('Merci ! Votre avis est publié.');
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === 'string'
                ? detail
                : (detail?.message || 'Envoi impossible');
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    // ── Rendus ──────────────────────────────────────────────
    if (loading) {
        return (
            <div className="min-h-screen bg-[#050308] flex items-center justify-center" data-testid="review-loading">
                <Loader2 className="w-8 h-8 text-[#A855F7] animate-spin" />
            </div>
        );
    }

    if (notFound || !invite) {
        return (
            <ErrorScreen
                title="Invitation introuvable"
                message="Ce lien est peut-être expiré ou déjà utilisé. Contactez votre agent pour en recevoir un nouveau."
            />
        );
    }

    if (submitted) {
        return <ThankYouScreen invite={invite} />;
    }

    const RoleIcon = invite.role === 'seller' ? Home : User;

    return (
        <div className="min-h-screen bg-[#050308] text-white pb-32 sm:pb-24" data-testid="public-review-page">
            {/* Halos DA Homly */}
            <div className="pointer-events-none fixed inset-0 overflow-hidden">
                <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-[#681793]/20 blur-[120px] rounded-full" />
                <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] bg-[#FF914D]/15 blur-[140px] rounded-full" />
            </div>

            <div className="relative max-w-2xl mx-auto px-4 sm:px-8 pt-10 sm:pt-14">
                {/* Header */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
                    <Link to="/" className="text-2xl font-extrabold bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                        Homly
                    </Link>
                    <div className="mt-6 inline-flex items-center gap-2 text-[10px] font-bold tracking-[3px] text-[#FF914D] px-3 py-1 rounded-full border border-[#A855F7]/25 bg-[#A855F7]/5">
                        <ShieldCheck className="w-3.5 h-3.5" />
                        AVIS VÉRIFIÉ HOMLY
                    </div>
                    <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold leading-tight">
                        Bonjour {invite.client_name.split(' ')[0]},<br />
                        <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                            votre retour compte
                        </span>
                    </h1>
                    <p className="mt-3 text-[#C7BEE6] text-base leading-relaxed">
                        <strong className="text-white">{invite.agent.name}</strong> souhaite votre avis
                        en tant que <RoleIcon className="w-3.5 h-3.5 inline align-baseline text-[#FF914D]" /> <strong>{invite.role_label}</strong>.
                        {invite.property_context && <> Bien concerné : <span className="text-white">{invite.property_context}</span>.</>}
                    </p>
                    {invite.personal_message && (
                        <div className="mt-5 p-4 rounded-2xl bg-[#A855F7]/10 border-l-2 border-[#A855F7]">
                            <div className="text-[10px] font-bold tracking-[2px] text-[#A855F7] mb-1">
                                UN MOT DE {invite.agent.name.toUpperCase()}
                            </div>
                            <p className="text-sm text-white italic leading-relaxed">« {invite.personal_message} »</p>
                        </div>
                    )}
                </motion.div>

                {/* Formulaire */}
                <motion.form
                    onSubmit={handleSubmit}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="rounded-3xl border border-[#A855F7]/20 bg-black/40 backdrop-blur-md p-6 sm:p-8 space-y-8"
                    data-testid="review-form"
                >
                    {/* Dimensions */}
                    <div>
                        <label className="block text-xs font-bold tracking-[2px] text-[#8A7DB8] mb-4">
                            NOTEZ CHAQUE DIMENSION (1 À 5 ÉTOILES)
                        </label>
                        <div className="space-y-4">
                            {Object.entries(invite.dimensions_meta).map(([key, label]) => (
                                <StarPicker
                                    key={key}
                                    label={label}
                                    value={dims[key]}
                                    onChange={(v) => setDims(d => ({ ...d, [key]: v }))}
                                    testid={`stars-${key}`}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Commentaire */}
                    <div>
                        <label className="block text-xs font-bold tracking-[2px] text-[#8A7DB8] mb-2">
                            VOTRE RESSENTI EN 2-3 LIGNES
                        </label>
                        <textarea
                            rows={4}
                            required minLength={10} maxLength={1200}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Racontez brièvement votre expérience : ce qui vous a marqué, ce que vous avez apprécié…"
                            className="w-full bg-black/40 border border-white/10 focus:border-[#A855F7]/50 focus:outline-none text-white placeholder:text-[#5A4C7A] rounded-xl px-4 py-3 text-sm resize-none"
                            data-testid="review-comment"
                        />
                        <div className="mt-1 text-[10px] text-[#6B5A99] text-right">
                            {comment.length}/1200 · min. 10 caractères
                        </div>
                    </div>

                    {/* Recommandation */}
                    <div>
                        <label className="block text-xs font-bold tracking-[2px] text-[#8A7DB8] mb-3">
                            RECOMMANDERIEZ-VOUS {invite.agent.name.split(' ')[0].toUpperCase()} ?
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            <RecommendButton
                                active={wouldRecommend === true}
                                onClick={() => setWouldRecommend(true)}
                                testid="rec-yes"
                                positive
                            >
                                <Heart className="w-4 h-4" /> Oui, sans hésiter
                            </RecommendButton>
                            <RecommendButton
                                active={wouldRecommend === false}
                                onClick={() => setWouldRecommend(false)}
                                testid="rec-no"
                            >
                                <XCircle className="w-4 h-4" /> Non
                            </RecommendButton>
                        </div>
                    </div>

                    {/* Consentement */}
                    <label className="flex items-start gap-2.5 text-xs text-[#C7BEE6] cursor-pointer p-3 rounded-xl border border-[#A855F7]/15 bg-black/30 hover:border-[#A855F7]/30" data-testid="consent-block">
                        <input
                            type="checkbox" required
                            checked={consentPublic}
                            onChange={(e) => setConsentPublic(e.target.checked)}
                            className="mt-0.5 accent-[#A855F7] w-4 h-4"
                            data-testid="consent-checkbox"
                        />
                        <span className="leading-snug">
                            <ShieldCheck className="w-3.5 h-3.5 inline text-[#FF914D] mr-1" />
                            J&apos;accepte que mon avis (prénom + commentaire + note) soit publié sur le profil
                            public de <strong className="text-white">{invite.agent.name}</strong>. Mon email
                            ne sera jamais visible.
                        </span>
                    </label>

                    {/* Submit (visible on desktop, mobile uses sticky bottom bar) */}
                    <button
                        type="submit"
                        disabled={!canSubmit || submitting}
                        data-testid="review-submit"
                        className="hidden sm:flex w-full items-center justify-center gap-2 px-6 py-4 rounded-full bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold text-base shadow-xl shadow-[#681793]/40 hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                    >
                        {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                        Publier mon avis
                    </button>

                    <p className="text-[10px] text-center text-[#6B5A99] tracking-wider">
                        HOMLY.PRO · AVIS 100% AUTHENTIFIÉS · IMPOSSIBLE À MODIFIER APRÈS PUBLICATION
                    </p>
                </motion.form>
            </div>

            {/* CTA sticky mobile — thumb-reach pour publier facilement */}
            <div className="sm:hidden fixed bottom-0 left-0 right-0 z-40 px-4 pb-[env(safe-area-inset-bottom)]" data-testid="mobile-sticky-cta">
                <div className="bg-[#050308]/95 backdrop-blur-xl border-t border-[#A855F7]/20 -mx-4 px-4 pt-3 pb-4">
                    {/* Progress bar */}
                    <div className="flex items-center justify-between mb-2 text-[10px] font-semibold">
                        <span className="text-[#8A7DB8] tracking-[1.5px]">{filledCount}/7 CHAMPS REMPLIS</span>
                        <span className={progress === 100 ? 'text-[#FF914D]' : 'text-[#8A7DB8]'}>{progress}%</span>
                    </div>
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mb-3">
                        <div
                            className="h-full bg-gradient-to-r from-[#681793] to-[#FF914D] transition-all duration-300"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={!canSubmit || submitting}
                        data-testid="review-submit-mobile"
                        className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold text-sm shadow-lg shadow-[#681793]/40 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity active:scale-[0.98]"
                    >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        {canSubmit ? 'Publier mon avis' : 'Compléter le formulaire'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Sous-composants ─────────────────────────────────────────
const StarPicker = ({ label, value, onChange, testid }) => (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <span className="text-sm text-white leading-tight">{label}</span>
        <div className="flex gap-0.5" data-testid={testid}>
            {[1, 2, 3, 4, 5].map(n => (
                <button
                    key={n}
                    type="button"
                    onClick={() => onChange(n)}
                    aria-label={`${n} étoile${n > 1 ? 's' : ''}`}
                    className="p-2 -m-1 group active:scale-90 transition-transform touch-manipulation"
                >
                    <Star
                        className={`w-7 h-7 sm:w-6 sm:h-6 transition-all group-hover:scale-110 ${
                            n <= value
                                ? 'fill-[#FF914D] text-[#FF914D]'
                                : 'text-[#3A2B5A] hover:text-[#8A7DB8]'
                        }`}
                    />
                </button>
            ))}
        </div>
    </div>
);

const RecommendButton = ({ children, active, onClick, positive, testid }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className={`inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all border ${
            active
                ? positive
                    ? 'bg-gradient-to-r from-[#681793] to-[#FF914D] border-transparent text-white shadow-lg shadow-[#681793]/30'
                    : 'bg-white/10 border-white/25 text-white'
                : 'bg-white/5 border-white/10 text-[#C7BEE6] hover:bg-white/10 hover:border-[#A855F7]/40'
        }`}
    >
        {children}
    </button>
);

const ThankYouScreen = ({ invite }) => (
    <div className="min-h-screen bg-[#050308] text-white flex items-center justify-center p-4" data-testid="thank-you-screen">
        <div className="pointer-events-none fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-radial from-[#A855F7]/10 via-transparent to-transparent" />
        </div>
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative text-center max-w-lg"
        >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center shadow-2xl shadow-[#681793]/40">
                <CheckCircle2 className="w-10 h-10 text-white" strokeWidth={2.5} />
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold mb-4">
                Merci{' '}
                <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                    {invite.client_name.split(' ')[0]} !
                </span>
            </h1>
            <p className="text-[#C7BEE6] mb-6 text-lg leading-relaxed">
                Votre avis vient d&apos;être publié sur le profil public de{' '}
                <strong className="text-white">{invite.agent.name}</strong>.
                Il aide déjà d&apos;autres clients à faire leur choix.
            </p>
            <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/10 bg-white/5 text-sm font-semibold text-[#C7BEE6] hover:bg-white/10 hover:text-white transition-colors"
            >
                Découvrir Homly
            </Link>
        </motion.div>
    </div>
);

const ErrorScreen = ({ title, message }) => (
    <div className="min-h-screen bg-[#050308] text-white flex items-center justify-center p-4" data-testid="error-screen">
        <div className="text-center max-w-md">
            <XCircle className="w-14 h-14 mx-auto mb-4 text-[#FFA48D]" />
            <h1 className="text-2xl font-extrabold mb-3">{title}</h1>
            <p className="text-sm text-[#C7BEE6] mb-6">{message}</p>
            <Link to="/" className="text-sm font-semibold text-[#FF914D] hover:underline">
                Retour à Homly
            </Link>
        </div>
    </div>
);
